// src/components/strumenti/UiStrumento.js
// Mattoncini grafici comuni agli strumenti aggiunti in W442 (stesso stile degli strumenti esistenti:
// campi «inp/lbl», riquadro del risultato col colore del settore, nota con formula e ipotesi, «Azzera»).
// Gli strumenti esistenti non li usano e restano come sono.
import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import { leggiNumero } from "../../utils/numeriStrumenti";

export const inp = { padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%", boxSizing:"border-box" };
export const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

// Colori dei settori (gli stessi della pagina Strumenti tecnici)
export const COLORI = {
  elettrico: { c:"#7c3aed", s:"#faf5ff", b:"#e9d5ff" }, fotovoltaico: { c:"#c2410c", s:"#fff7ed", b:"#fed7aa" },
  illuminazione: { c:"#d97706", s:"#fffbeb", b:"#fde68a" }, antintrusione: { c:"#dc2626", s:"#fef2f2", b:"#fecaca" },
  tvcc: { c:"#6d28d9", s:"#f5f3ff", b:"#ddd6fe" }, condizionamento: { c:"#0891b2", s:"#ecfeff", b:"#a5f3fc" },
  idraulico: { c:"#2563eb", s:"#eff6ff", b:"#bfdbfe" }, reti: { c:"#0369a1", s:"#eff6ff", b:"#bfdbfe" },
  geometra: { c:"#15803d", s:"#f0fdf4", b:"#bbf7d0" }, matematica: { c:"#6d28d9", s:"#f5f3ff", b:"#ddd6fe" },
  amministrativo: { c:"#059669", s:"#ecfdf5", b:"#a7f3d0" }, finanza: { c:"#0369a1", s:"#eff6ff", b:"#bfdbfe" },
};

/** Stato dei campi: valori come testo (così «8,5» resta scritto com'è); n(k) li legge come numero. */
export function useCampi(iniziali) {
  const [v, setV] = useState(iniziali);
  const set = (k) => (e) => { const val = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e; setV(o => ({ ...o, [k]: val })); };
  const n = (k) => leggiNumero(v[k]);
  return { v, set, n, azzera: () => setV(iniziali), setV };
}

export const fmt = (x, dec = 2) => (x === null || x === undefined || !Number.isFinite(x) ? "—" : x.toLocaleString("it-IT", { maximumFractionDigits: dec }));
export const euro = (x) => (x === null || x === undefined || !Number.isFinite(x) ? "—" : "€ " + x.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export function Campo({ etichetta, valore, onChange, placeholder, tipo = "text", aiuto }) {
  return (
    <div>
      <label style={lbl}>{etichetta}
        <input type={tipo} value={valore} onChange={onChange} placeholder={placeholder} style={{ ...inp, marginTop:4 }} inputMode={tipo === "text" ? "decimal" : undefined}/>
      </label>
      {aiuto && <div style={{ fontSize:10, color:"#9ca3af", marginTop:3 }}>{aiuto}</div>}
      {tipo === "text" && dubbioMigliaia(valore) && <div style={{ fontSize:10, color:"#b45309", marginTop:3 }}>«{String(valore).trim()}» è letto come {leggiNumero(valore).toLocaleString("it-IT")} (il punto da solo è il decimale): per {Number(String(valore).replace(/\D/g, "")).toLocaleString("it-IT")} scrivi «{String(valore).replace(/\D/g, "")}» oppure «{String(valore).trim()},00».</div>}
    </div>
  );
}

/** «1.000», «12.500»: il punto da solo è il decimale (come prima), ma potrebbe essere inteso come migliaia. */
export const dubbioMigliaia = (v) => /^\s*[1-9]\d{0,2}\.\d{3}\s*$/.test(String(v ?? ""));

export function Scelta({ etichetta, valore, onChange, opzioni }) {
  return (
    <div>
      <label style={lbl}>{etichetta}
        <select value={valore} onChange={onChange} style={{ ...inp, marginTop:4 }}>
          {opzioni.map(o => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    </div>
  );
}

export function Testo({ etichetta, valore, onChange, placeholder, righe = 5, aiuto }) {
  return (
    <div>
      <label style={lbl}>{etichetta}
        <textarea value={valore} onChange={onChange} placeholder={placeholder} rows={righe} style={{ ...inp, marginTop:4, resize:"vertical", fontFamily:"'Inter',sans-serif" }}/>
      </label>
      {aiuto && <div style={{ fontSize:10, color:"#9ca3af", marginTop:3 }}>{aiuto}</div>}
    </div>
  );
}

export const Griglia = ({ colonne = 2, children }) => (
  <div style={{ display:"grid", gridTemplateColumns:`repeat(${colonne}, minmax(0, 1fr))`, gap:12, marginBottom:12 }}>{children}</div>
);

/** Riquadro del risultato: titolo, valore principale e righe di dettaglio [etichetta, valore]. */
export function Risultato({ settore, titolo, valore, unita, righe = [] }) {
  const k = COLORI[settore] || COLORI.matematica;
  return (
    <div role="status" aria-label={titolo} style={{ background:k.s, border:`1px solid ${k.b}`, borderRadius:10, padding:"14px 16px", marginTop:4 }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, marginBottom:4 }}>{titolo}</div>
        <div style={{ fontSize:22, fontWeight:800, color:k.c }}>{valore}{unita && valore !== "—" && <span style={{ fontSize:14, marginLeft:6, color:"#9ca3af" }}>{unita}</span>}</div>
      </div>
      {righe.filter(Boolean).length > 0 && (
        <table style={{ width:"100%", fontSize:12, marginTop:10, borderTop:`1px solid ${k.b}`, paddingTop:6 }}>
          <tbody>
            {righe.filter(Boolean).map(([a, b]) => (
              <tr key={a}><td style={{ color:"#6b7280", padding:"3px 0" }}>{a}</td><td style={{ textAlign:"right", fontWeight:700, color:"#374151" }}>{b}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export const Avviso = ({ children }) => (
  <div role="alert" style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"7px 10px", marginBottom:10, fontSize:11, color:"#991b1b" }}>{children}</div>
);

/** Formula e ipotesi del calcolo, sempre visibili sotto il risultato. */
export const Formula = ({ children }) => (
  <div style={{ fontSize:10.5, color:"#6b7280", marginTop:10, lineHeight:1.55, background:"#f9fafb", border:"1px solid #f3f4f6", borderRadius:8, padding:"8px 10px" }}>{children}</div>
);

export const Azzera = ({ onClick }) => (
  <button onClick={onClick} style={{ marginTop:12, display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
    <RefreshCw size={12}/> Azzera
  </button>
);

/** Interruttore a due o più modalità (come kVA → kW / kW → kVA). */
export function Modi({ settore, valore, onChange, opzioni }) {
  const k = COLORI[settore] || COLORI.matematica;
  return (
    <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
      {opzioni.map(([id, testo]) => (
        <button key={id} type="button" aria-pressed={valore === id} onClick={() => onChange(id)} style={{ flex:1, minWidth:120, padding:"8px", borderRadius:8, border:`1px solid ${valore === id ? k.c : "#e5e7eb"}`, background: valore === id ? k.s : "#fff", color: valore === id ? k.c : "#6b7280", fontSize:12, fontWeight:700, cursor:"pointer" }}>{testo}</button>
      ))}
    </div>
  );
}
